/**
 * Fetch-based SSE client.
 *
 * The native EventSource API does not support custom headers, which would force
 * the auth token into the query string (a security risk). This utility uses
 * fetch() so the token can be sent in the Authorization header instead.
 */
export async function fetchSSE(
  url: string,
  onMessage: (data: unknown) => void,
  signal: AbortSignal
): Promise<void> {
  const token = localStorage.getItem('access_token');

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream',
    },
    signal,
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`SSE request failed with status ${response.status}${body ? `: ${body}` : ''}`);
  }

  if (!response.body) {
    throw new Error('SSE response has no body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            onMessage(JSON.parse(line.slice(6)));
          } catch {
            // skip malformed event lines
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
